import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, BookMarked, Trash2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";

export default function Bibliography() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", authors: "", year: "", venue: "", doi: "", notes: "" });

  const load = () => api.get("/bibliography").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim()) return;
    try { await api.post("/bibliography", form); toast.success("Référence ajoutée"); setOpen(false); setForm({ title: "", authors: "", year: "", venue: "", doi: "", notes: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { await api.delete(`/bibliography/${id}`); load(); };
  const cite = (b) => {
    const c = `${b.authors || "Auteur inconnu"} (${b.year || "s.d."}). ${b.title}. ${b.venue || ""}. ${b.doi ? "https://doi.org/" + b.doi : ""}`.trim();
    navigator.clipboard.writeText(c); toast.success("Citation copiée (APA)");
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Bibliographie</h1><p className="text-muted2 mt-1">Gérez vos références et exportez vos citations.</p></div>
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-800 rounded-full gap-2" data-testid="biblio-add-btn"><Plus className="h-4 w-4" /> Ajouter</Button>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 flex items-start justify-between gap-4" data-testid={`biblio-${b.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy">{b.title}</p>
              <p className="text-sm text-muted2 mt-0.5">{b.authors} {b.year && `(${b.year})`}</p>
              {b.venue && <p className="text-xs italic text-muted2">{b.venue}</p>}
              {b.doi && <p className="text-xs font-mono text-muted2 mt-1">DOI: {b.doi}</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => cite(b)} className="text-muted2 hover:text-navy p-1.5" title="Copier citation APA" data-testid={`cite-${b.id}`}><Copy className="h-4 w-4" /></button>
              <button onClick={() => remove(b.id)} className="text-muted2 hover:text-red-500 p-1.5"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-muted2"><BookMarked className="h-8 w-8 mx-auto mb-3" />Aucune référence. Ajoutez-en depuis la Recherche scientifique.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="biblio-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Nouvelle référence</DialogTitle><DialogDescription>Renseignez les métadonnées de la référence bibliographique.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="biblio-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Auteurs</Label><Input value={form.authors} onChange={(e) => setForm({ ...form, authors: e.target.value })} className="mt-1" /></div>
              <div><Label>Année</Label><Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Revue / Source</Label><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="mt-1" /></div>
              <div><Label>DOI</Label><Input value={form.doi} onChange={(e) => setForm({ ...form, doi: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={add} className="bg-navy text-white hover:bg-navy-800 rounded-full w-full" data-testid="biblio-submit">Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
